import React, { useMemo, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Col, Row, Card, CardBody } from "reactstrap";
import { useNavigate } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import { CustomTable } from "@components/custom-table/table";
import { resetDelete } from "@helpers/custom-delete/controller/delete-slice";
import { url } from "@constants/url-constants";
import {
  resetError,
  resetState,
  resetSuccess,
} from "../controller/author-slice";
import { getAuthorList } from "../services/author-services";
import { S3_BASE_URL } from "../../../constants/constants";
import DeleteModal from "@components/custom-table/DeleteModal";
import { STORAGE_KEY } from "../../../constants/constants.js";
import { MASTER_DELETE_ID, MASTER_EDIT_ID, MEDIA_CREATE_ID } from "../../../helpers/permission/constant-permissions";

export const Author = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const authors = useSelector((state) => state.author.authors);
  const pagination = useSelector((state) => state.author.pagination);
  const isLoading = useSelector((state) => state.author.loading);
  const responseMessage = useSelector((state) => state.author.responseMessage);
  const successMessage = useSelector((state) => state.author.successMessage);
  const deleteMessage = useSelector((state) => state.delete.successMessage);
  const currentPage = pagination?.current_page;
  const deleteUrl = url.authors;
  const [deleteModel, setDeleteModel] = useState(false);
  const [deleteData, setDeleteData] = useState({});
  const userData = JSON.parse(localStorage.getItem(STORAGE_KEY.USER));

  useEffect(() => {
    if (responseMessage !== null) {
      dispatch(resetState());
    }
  }, [dispatch, responseMessage]);

  useEffect(() => {
    if (deleteMessage !== null) {
      toast.success(deleteMessage);

      dispatch(resetDelete());
    }
  }, [dispatch, deleteMessage]);

  useEffect(() => {
    if (successMessage !== null) {
      toast.success(successMessage);

      dispatch(resetSuccess());
    }
  }, [dispatch, successMessage]);

  const handleDeleteAuthor = ({ id, url, deleted_at }) => {
    setDeleteModel(true);
    setDeleteData({ id, url, deleted_at });
  };

  const handleCreateAuthor = () => {
    navigate(`/author/add`);
    dispatch(resetError());
  };

  const handleEditAuthor = ({ id, page }) => {
    navigate(`/author/edit/${id}?current_page=${page}`);
    dispatch(resetError());
  };

  const columns = useMemo(
    () => {
      const cols = [
        {
          header: "Sorting ",
          accessorKey: "sorting",
          enableColumnFilter: false,
          enableSorting: true,
        },
        {
          header: "Author",
          accessorKey: "name",
          enableColumnFilter: false,
          enableSorting: true,
        },
        {
          header: "Image",
          accessorKey: "image_url",
          enableColumnFilter: false,
          enableSorting: true,
          cell: (cellProps) => {
            return cellProps.row.original?.thumbnail?.image_url ? (
              <img
                src={S3_BASE_URL + cellProps.row.original?.thumbnail?.image_url}
                alt=""
                className="avatar-sm rounded-circle img-thumbnail"
              />
            ) : null;
          },
        },

      ]
      if (userData?.permission_ids?.includes(MASTER_EDIT_ID) || userData?.permission_ids?.includes(MASTER_DELETE_ID)) {
        cols.push({
          header: "Action",
          enableColumnFilter: false,
          enableSorting: true,
          cell: (cellProps) => {
            return (
              <ul className="list-unstyled hstack gap-2  d-flex justify-content-center mb-0">
                {userData?.permission_ids?.includes(MASTER_EDIT_ID) && (<li data-bs-toggle="tooltip" data-bs-placement="top" title="Edit">
                  <button
                    className="btn btn-sm btn-soft-info"
                    onClick={() => {
                      if (cellProps.row.original.deleted_at === null) {
                        handleEditAuthor({
                          id: cellProps.row.original._id,
                          page: currentPage,
                        });
                      } else {
                        toast.error("Record Not found");
                      }
                    }}
                    id={`edit-tooltip-${cellProps.row.original.id}`}
                  >
                    <i className="mdi mdi-pencil" />
                  </button>
                </li>)}

                {userData?.permission_ids?.includes(MASTER_DELETE_ID) && (<li
                  data-bs-toggle="tooltip"
                  data-bs-placement="top"
                  title={
                    cellProps.row.original.deleted_at === null
                      ? "Delete"
                      : "Restore"
                  }
                >
                  <button
                    className={`btn btn-sm ${cellProps.row.original.deleted_at === null
                      ? "btn btn-sm btn-soft-danger"
                      : "btn-soft-success"
                      }`}
                    // onClick={() => dispatch(deleteCountry(cellProps.row.original._id))}
                    onClick={() =>
                      handleDeleteAuthor({
                        id: cellProps.row.original._id,
                        url: deleteUrl,
                        deleted_at: cellProps.row.original.deleted_at,
                      })
                    }
                    id={`delete-tooltip-${cellProps.row.original.id}`}
                  >
                    <i className="mdi mdi-delete-outline" />
                  </button>
                </li>)}
              </ul>
            );
          },
        },)
      }

      return cols
    },
    [currentPage, deleteUrl]
  );

  return (
    <React.Fragment>
      <DeleteModal
        show={deleteModel}
        onDeleteClick={handleDeleteAuthor}
        onCloseClick={() => setDeleteModel(false)}
        data={deleteData}
      />
      <div className="page-content">
        <ToastContainer autoClose={2000} />
        <div className="container-fluid">
          {
            <Row>
              <Col lg="12">
                <Card>
                  <CardBody className="">
                    <div className="d-flex align-items-center text-start">
                      <h5 className="mb-0 card-title flex-grow-1">
                        Author List
                      </h5>
                    </div>
                  </CardBody>
                  <CardBody>
                    <CustomTable
                      loading={isLoading}
                      columns={columns}
                      data={authors || []}
                      isCustomPageSize={true}
                      isGlobalFilter={true}
                      isJobListGlobalFilter={true}
                      isPagination={true}
                      isAddButton={true}
                      page={currentPage}
                      isAddPermission={userData?.permission_ids?.includes(MEDIA_CREATE_ID)}
                      paginationCount={pagination?.total_count}
                      handleUserClick={handleCreateAuthor}
                      handleList={getAuthorList}
                      isEpisode={false}
                      paginationLimit={pagination}
                      buttonClass="btn btn-success"
                      buttonName="Add"
                      theadClass="thead-light"
                      SearchPlaceholder="Search for ..."
                      tableClass="align-middle table-nowrap dt-responsive nowrap w-100 table-check dataTable no-footer dtr-inline mt-4 border-top"
                      pagination="pagination"
                      paginationWrapper="dataTables_paginate paging_simple_numbers pagination-rounded"
                    />
                  </CardBody>
                </Card>
              </Col>
            </Row>
          }
        </div>
      </div>
    </React.Fragment>
  );
};
